import loadable from '@loadable/component';
import get from 'lodash/get';
import submitModalStore from 'stores/BackInStock/Modal/SubmitModalStore';
import submittedModalStore from 'stores/BackInStock/Modal/SubmittedModalStore';
import pageStore from 'stores/BackInStock/PageStore';
import visitorStore from 'stores/Visitor/VisitorStore';
import * as bisUtils from "common/backinstock-utils";
import * as appUtils from 'stores/BackInStock/Utils';
import * as constants from 'Constants';
import * as api from 'Api';
import * as utils from 'Utils';
import apiReady from './ready';


// Note:
// Can't use `utils.isProduction()` to check production mode. If you use that samples
// will always get included into assets for some unknown reasons.
let samples = {};
if (process.env.NODE_ENV !== 'production') {
  samples = require('./samples').default;
}


class BackInStockLoader {

  constructor() {}

  renderDOM = async () => {
    if (window.OnVoardBackInStock['rendered']) return;
    if (!submitModalStore.display || !pageStore.selector) return;

    let React = await import('react');
    let ReactDOM = await import('react-dom');
    const App = loadable(() => import('./app'));

    ReactDOM.render(<App />, document.querySelector("#ov_app_back_in_stock"));
    window.OnVoardBackInStock['rendered'] = true;
  };

  onLoadModalDisplay = () => {
    const product = pageStore.product;
    const modalConfig = pageStore.modalConfig;
    let productId = `${product.id}`;

    if (!modalConfig.displayOnLoad) return;
    if (!product.outOfStock) return;
    if (!appUtils.isProductPage()) return;
    if (visitorStore.productsPrompted.includes(productId)) return;
    submitModalStore.show();
    this.renderDOM();
  };

  onReadyTasks = async () => {
    await appUtils.loadProductData();
  };

  cleanupEventListeners = () => {
    document.removeEventListener('click', this.setupBackInStockTrigger);
  };

  setupEventListeners = () => {
    if (!utils.isProduction()) return;
    document.addEventListener('click', this.setupBackInStockTrigger);
  };

  setupBackInStockTrigger = async (evt) => {
    var target = evt.target;
    if (!target) return;

    let matches = false;
    if (target.classList.contains('ov-bis-trigger')) matches = true;
    if (!matches) {
      let ancestor = target.closest(`.ov-bis-trigger`);
      if (ancestor) {
        target = ancestor;
        matches = true;
      }
    }

    if (matches) {
      evt.preventDefault();
      await appUtils.setProductFromTrigger(target);
      if (!pageStore.product.id) return;
      window.OnVoard("back_in_stock", "showModal");
    }
  };

  loadBackInStock = async () => {
    const el = document.querySelector(".ov-app-back-in-stock");
    if (!el) return;

    if (!utils.isProduction()) {
      await pageStore.deserialize(samples.s1);
      submitModalStore.show();
      apiReady(this.renderDOM);

      await this.onReadyTasks();
      appUtils.loadBackInStockCustomStyles(el);
      this.renderDOM();
      return;
    }

    if (utils.isPreview()) {
      await pageStore.deserialize(window.OV_PREVIEW_DATA);

      await this.onReadyTasks();
      appUtils.loadBackInStockCustomStyles(el);
      submitModalStore.show();
      this.renderDOM();
      return;
    }

    const successCallback = async (response) => {
      await pageStore.deserialize(response);
      if (response.visitor) await visitorStore.deserialize(response.visitor);
      apiReady(this.renderDOM);

      await this.onReadyTasks();
      appUtils.loadBackInStockCustomStyles(el);
      this.onLoadModalDisplay();
    };

    const errorCallback = (errors) => {
      utils.printErrors(errors);
    };

    let qs = appUtils.getPayloadQs();
    api.getBackInStockPayloads(successCallback, errorCallback, pageStore.id, qs);
  };

  resetStores = () => {
    submitModalStore.reset();
    pageStore.reset();
    visitorStore.reset();
  };

  setup = () => {
    const el = document.querySelector(".ov-app-back-in-stock");
    if (el) {
      let appId = bisUtils.getAppId(el);
      if (appId) pageStore.setId(appId);
    }

    if (utils.isPreview()) this.setupPreview();
  };

  setupPreview = () => {
    if (!utils.isPreview()) return;
    if (!window.OV_PREVIEW_DATA) window.OV_PREVIEW_DATA = {};

    window.ovReloadPreview = async () => {
      const el = document.querySelector(".ov-app-back-in-stock");
      if (!el) return;

      await pageStore.deserialize(window.OV_PREVIEW_DATA);
      appUtils.loadBackInStockCustomStyles(el);

      // must set directly since `hide` method won't work for preview
      if (window.OV_PREVIEW_SUBMITTED) {
        submitModalStore.setDisplay(false);
        submittedModalStore.setDisplay(true);
      } else {
        submitModalStore.setDisplay(true);
        submittedModalStore.setDisplay(false);
      }

      this.renderDOM();
    };
  };

  start = async () => {
    this.resetStores();
    window.OnVoardBackInStock = {
      'rendered': false,
    };

    // need cleanup to avoid duplicates
    this.cleanupEventListeners();
    this.setupEventListeners();

    this.setup();
    await this.loadBackInStock();
  };
}

export function load() {
  let loader = new BackInStockLoader();
  loader.start();
}

